// src/component/GroupActivity2026DialogLauncher.js
//
// 表示判定：
//  - 現状（テスト中）：users/{uid}.isAdmin === true のユーザーにのみ表示。
//  - 将来：4期ユーザー（users/{uid}.term === 4）全員に展開予定。
//  - systemConfig/groupActivity2026Dialog.enabled === false なら表示しない。
//  - users/{uid}.groupActivity2026.response が記録されたら表示しない。
//  - 未回答ユーザーには「1日1回」だけ表示（localStorage で同日中の再表示を抑止）。
//
// 文言・回答期日は systemConfig/groupActivity2026Dialog から取得。

import React, { useEffect, useState } from "react";
import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../api/firebase";
import GroupActivity2026Dialog from "./GroupActivity2026Dialog";
import {
  GROUP_ACTIVITY_2026_CONFIG_DOC,
  GROUP_ACTIVITY_2026_DEFAULTS,
  GA2026_AUDIENCE,
} from "./groupActivity2026Defaults";
import { formatDeadlineJa } from "./groupActivity2026Format";
import { ensureServerTimeSynced } from "../api/serverTime";
import { canSeeGroupActivity2026 } from "./groupActivity2026Audience";

const LAST_SHOWN_KEY = (uid) => `groupActivity2026LastShown_${uid}`;

function isSameDay(d1, d2) {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

export default function GroupActivity2026DialogLauncher() {
  const [userReady, setUserReady] = useState(false);
  const [configReady, setConfigReady] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false);
  const [userData, setUserData] = useState(null);
  const [hasResponded, setHasResponded] = useState(false);

  const [audience, setAudience] = useState(GROUP_ACTIVITY_2026_DEFAULTS.audience);
  const [enabled, setEnabled] = useState(true);
  const [chipLabel, setChipLabel] = useState(GROUP_ACTIVITY_2026_DEFAULTS.chipLabel);
  const [heading, setHeading] = useState(GROUP_ACTIVITY_2026_DEFAULTS.heading);
  const [body, setBody] = useState(GROUP_ACTIVITY_2026_DEFAULTS.body);
  const [deadline, setDeadline] = useState(GROUP_ACTIVITY_2026_DEFAULTS.deadline);

  const [open, setOpen] = useState(false);
  const [serverTimeReady, setServerTimeReady] = useState(false);

  // サーバ時刻の同期（1回だけ／失敗時はローカル時計）
  useEffect(() => {
    let alive = true;
    ensureServerTimeSynced().finally(() => {
      if (alive) setServerTimeReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  // 設定ドキュメントを購読：ON/OFF & 文言 & 期日
  useEffect(() => {
    const ref = doc(db, "systemConfig", GROUP_ACTIVITY_2026_CONFIG_DOC);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() || {};
          setAudience(
            data.audience === GA2026_AUDIENCE.ALL
              ? GA2026_AUDIENCE.ALL
              : GA2026_AUDIENCE.ADMIN
          );
          setEnabled(data.enabled !== false); // 未定義は true
          setChipLabel(data.chipLabel ?? GROUP_ACTIVITY_2026_DEFAULTS.chipLabel);
          setHeading(data.heading ?? GROUP_ACTIVITY_2026_DEFAULTS.heading);
          setBody(data.body ?? GROUP_ACTIVITY_2026_DEFAULTS.body);
          setDeadline(data.deadline ?? GROUP_ACTIVITY_2026_DEFAULTS.deadline);
        } else {
          setAudience(GROUP_ACTIVITY_2026_DEFAULTS.audience);
          setEnabled(true);
          setChipLabel(GROUP_ACTIVITY_2026_DEFAULTS.chipLabel);
          setHeading(GROUP_ACTIVITY_2026_DEFAULTS.heading);
          setBody(GROUP_ACTIVITY_2026_DEFAULTS.body);
          setDeadline(GROUP_ACTIVITY_2026_DEFAULTS.deadline);
        }
        setConfigReady(true);
      },
      (err) => {
        console.error("GroupActivity2026DialogLauncher config error:", err);
        setEnabled(true);
        setConfigReady(true);
      }
    );
    return () => unsub();
  }, []);

  // users/{uid} を購読：isAdmin と groupActivity2026
  useEffect(() => {
    const current = auth.currentUser;
    if (!current) {
      setUserReady(true);
      return;
    }

    const ref = doc(db, "users", current.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() || {};
          setIsAdmin(!!data.isAdmin);
          setUserData(data);
          setHasResponded(!!data.groupActivity2026?.response);
        } else {
          setIsAdmin(false);
          setUserData(null);
          setHasResponded(false);
        }
        setUserReady(true);
      },
      (err) => {
        console.error("GroupActivity2026DialogLauncher user error:", err);
        setUserReady(true);
      }
    );

    return () => unsub();
  }, []);

  // 公開範囲：admin なら isAdmin 必須、all なら 4期支払い済みユーザー（管理者は常に可）
  const audienceAllows = canSeeGroupActivity2026({
    audience,
    isAdmin,
    userData,
  });

  // 条件が揃ったら open（日次once）
  useEffect(() => {
    if (!userReady || !configReady || !serverTimeReady) return;
    if (!enabled || !audienceAllows || hasResponded) {
      setOpen(false);
      return;
    }

    // 回答期日を過ぎていたらダイアログは出さない（サーバ時刻基準）
    const deadlineInfo = formatDeadlineJa(deadline);
    if (deadlineInfo && deadlineInfo.isPast) {
      setOpen(false);
      return;
    }

    const current = auth.currentUser;
    if (!current) return;

    const key = LAST_SHOWN_KEY(current.uid);
    const last = localStorage.getItem(key);
    const today = new Date();

    let shouldOpen = false;
    if (!last) {
      shouldOpen = true;
    } else {
      const lastDate = new Date(last);
      if (Number.isNaN(lastDate.getTime()) || !isSameDay(lastDate, today)) {
        shouldOpen = true;
      }
    }

    if (shouldOpen) {
      setOpen(true);
      localStorage.setItem(key, today.toISOString());
    }
  }, [userReady, configReady, serverTimeReady, enabled, audienceAllows, hasResponded, deadline]);

  const markShownToday = () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    localStorage.setItem(LAST_SHOWN_KEY(uid), new Date().toISOString());
  };

  const handleClose = () => {
    markShownToday();
    setOpen(false);
  };

  const handleRespond = async (response) => {
    const current = auth.currentUser;
    if (!current) return;
    try {
      const ref = doc(db, "users", current.uid);
      await setDoc(
        ref,
        {
          groupActivity2026: {
            response, // "join" | "decline"
            respondedAt: serverTimestamp(),
          },
        },
        { merge: true }
      );
      markShownToday();
      setOpen(false);
    } catch (e) {
      console.error("GroupActivity2026 respond failed:", e);
    }
  };

  if (!userReady || !configReady || !serverTimeReady) return null;
  if (!enabled) return null;
  if (!audienceAllows) return null;
  // 期日経過後はダイアログをマウントしない（モーダル DOM 自体を生成しない）
  {
    const info = formatDeadlineJa(deadline);
    if (info && info.isPast) return null;
  }

  return (
    <GroupActivity2026Dialog
      open={open}
      onClose={handleClose}
      onRespond={handleRespond}
      chipLabel={chipLabel}
      heading={heading}
      body={body}
      deadline={deadline}
      isAdminOnly={audience === GA2026_AUDIENCE.ADMIN}
    />
  );
}
